import type { FC } from "react";

import { useDropzone } from "react-dropzone";
import type { UseFormReturn } from "react-hook-form";

import { Button, Icon, Input, Select } from "@alpacahq/ui/old";

import { Field } from "~/v2/components/form";
import type { WireSchema } from "./schema";

type WireFormFieldsProps = {
  form: UseFormReturn<WireSchema>;
  countryList: { key: string; label: string }[];
};

export const WireFormFields: FC<WireFormFieldsProps> = ({
  form,
  countryList,
}) => {
  const {
    setError,
    setValue,
    clearErrors,
    formState: { errors },
    watch,
    register,
  } = form;

  const dropzone = useDropzone({
    maxFiles: 1,
    maxSize: 20000000,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }
      setValue("bank_statement", acceptedFiles[0]);
    },
    onDropRejected: (fileRejection) => {
      if (
        fileRejection.length > 0 &&
        fileRejection[0]?.errors[0]?.code === "file-too-large"
      ) {
        setError("bank_statement", { message: "File is too large" });
      }
    },
  });

  const handleRemoveBankStatement = () => {
    // @ts-expect-error: gotta be someway to remove File without marking optional??
    setValue("bank_statement", undefined);
    clearErrors("bank_statement");
  };

  const handleChangeInternational = (isInternational: boolean) => () => {
    setValue("isInternational", isInternational);
    setValue("bank_code_type", isInternational ? "BIC" : "ABA");
  };

  const bankStatement = watch("bank_statement");
  const isInternational = watch("isInternational");

  return (
    <>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={handleChangeInternational(false)}
            variant={isInternational ? "ghost" : "green"}
          >
            US bank
          </Button>
          <Button
            type="button"
            onClick={handleChangeInternational(true)}
            variant={isInternational ? "green" : "ghost"}
          >
            International bank
          </Button>
        </div>
      </div>
      <div className="mb-4">
        <Field
          label="Bank Name"
          error={!!errors.name}
          errorMsg={errors.name?.message}
          required={true}
        >
          <Input
            placeholder="Example Bank"
            {...register("name", {
              required: "Required",
            })}
          />
        </Field>
      </div>
      <div className="mb-4">
        <Field
          label="Account Number"
          error={!!errors.account_number}
          errorMsg={errors.account_number?.message}
          required={true}
        >
          <Input
            placeholder="123456789"
            {...register("account_number", {
              required: "Required",
            })}
          />
        </Field>
      </div>
      <div className="mb-4">
        <Field
          label={!isInternational ? "Routing Number" : "SWIFT/BIC Code"}
          error={!!errors.bank_code}
          errorMsg={errors.bank_code?.message}
          required={true}
        >
          <Input
            placeholder="EXAMPLE1234"
            {...register("bank_code", {
              required: "Required",
            })}
          />
        </Field>
      </div>
      <div className="mb-4">
        <Field
          label={"Bank Statement"}
          error={!!errors.bank_statement}
          errorMsg={errors.bank_statement?.message}
          required={true}
        >
          <div
            {...dropzone.getRootProps()}
            className="my-4 flex cursor-pointer items-center justify-center rounded border-2 border-gray-900 border-dashed p-5 transition-all hover:bg-gray-300"
          >
            <input
              placeholder="Bank Statement"
              {...register("bank_statement", {
                required: "Required",
              })}
              {...dropzone.getInputProps()}
            />
            {bankStatement?.name ? (
              <Button
                aria-label={`Remove document ${bankStatement?.name}`}
                onClick={handleRemoveBankStatement}
                className="m-2 mr-3"
              >
                <Icon name="Trash" size="sm" />
              </Button>
            ) : (
              <Icon
                name="ArrowDownTray"
                size="sm"
                variant="line"
                aria-label="upload-document"
                className="m-2"
              />
            )}
            <div className="flex items-center justify-center font-medium text-gray-600">
              {bankStatement?.name ||
                "Drag and drop here, or click to select file."}
            </div>
          </div>
        </Field>
      </div>
      {isInternational && (
        <>
          <div className="mb-4">
            <Field
              label="Country"
              error={!!errors.country}
              errorMsg={errors.country?.message}
              required={true}
            >
              <Select
                multiple={false}
                className="w-full"
                options={countryList.map(({ key, label }) => ({
                  value: key,
                  label,
                }))}
                id="country"
                value={watch("country")}
                onChange={(value) => setValue("country", value)}
              />
            </Field>
          </div>
          <div className="mb-4">
            <Field
              label="Street Address"
              error={!!errors.street_address}
              errorMsg={errors.street_address?.message}
              required={true}
            >
              <Input
                placeholder="123 Main St"
                {...register("street_address", {
                  required: "Required",
                })}
              />
            </Field>
          </div>
          <div className="mb-4 flex gap-4">
            <div>
              <Field
                label="City"
                error={!!errors.city}
                errorMsg={errors.city?.message}
                required={true}
              >
                <Input
                  placeholder="San Francisco"
                  {...register("city", {
                    required: "Required",
                  })}
                />
              </Field>
            </div>
            <div>
              <Field
                label="State"
                error={!!errors.state_province}
                errorMsg={errors.state_province?.message}
                required={true}
              >
                <Input
                  placeholder="CA"
                  {...register("state_province", {
                    required: "Required",
                  })}
                />
              </Field>
            </div>
            <div>
              <Field
                label="Postal Code"
                error={!!errors.postal_code}
                errorMsg={errors.postal_code?.message}
                required={true}
              >
                <Input
                  placeholder="94105"
                  {...register("postal_code", {
                    required: "Required",
                  })}
                />
              </Field>
            </div>
          </div>
          <div>
            <Field
              label="Additional Information"
              error={!!errors.additional_information}
              errorMsg={errors.additional_information?.message}
            >
              <Input
                placeholder="Enter more information here"
                {...register("additional_information")}
              />
            </Field>
          </div>
        </>
      )}
    </>
  );
};
